import React from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useIsFetching } from '@tanstack/react-query';
import { Home, Upload, Search, Clock } from 'lucide-react';
import DashboardHeader from './DashboardHeader';
import ExamWishModal from './ExamWishModal';
import { useAuth } from '../../context/AuthContext';
import { ROUTES } from '../../lib/constants';

export const DashboardLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  // Under-the-hood tracking hook: returns an integer > 0 if ANY data queries are syncing in the background
  const isFetchingGlobal = useIsFetching();

  const mobileNavItems = [
    { icon: Home, path: ROUTES.DASHBOARD, label: 'Dashboard' },
    { icon: Upload, path: ROUTES.UPLOAD, label: 'Upload' },
    { icon: Search, path: ROUTES.PAST_QUESTIONS, label: 'Past Qs' },
    { icon: Clock, path: ROUTES.HISTORY, label: 'History' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream-50 via-cream-100 to-brown-50 flex flex-col pb-20 md:pb-0">
      {/* Universal Sticky Top Header Bar */}
      <DashboardHeader />

      {/* GLOBAL LINEAR PROGRESS REVALIDATION OVERLAY INDEX LINE */}
      {isFetchingGlobal > 0 && (
        <div className="fixed top-16 left-0 right-0 h-[2.5px] bg-gradient-to-r from-transparent via-amber-500 to-transparent z-50 animate-pulse w-full" />
      )}

      {/* Center Main Dynamic View Mount Point */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-4 overflow-x-hidden">
        <Outlet />
      </main>

      {/* Exam Wish Pop-Up Modal */}
      <ExamWishModal 
        userId={user?.id} 
        isPremium={user?.is_premium || user?.app_metadata?.is_premium} 
      />

      {/* 4-Icon Mobile Bottom Navigation Bar (Hidden on Desktop) */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-lg border-t border-cream-300/40 shadow-[0_-4px_20px_rgba(0,0,0,0.03)] px-6 pb-safe">
        <div className="flex h-16 justify-between items-center mx-auto max-w-sm">
          {mobileNavItems.map((item) => {
            const IconComponent = item.icon;
            const isActive = location.pathname === item.path;

            return (
              <button
                key={item.label}
                onClick={() => navigate(item.path)}
                className="flex flex-col items-center justify-center relative py-2 px-3 transition-transform active:scale-90"
              >
                <div className={`p-1 transition-colors duration-200 ${
                  isActive ? 'text-brown-900' : 'text-brown-400'
                }`}>
                  <IconComponent className="w-6 h-6 stroke-[2.2]" />
                </div>
                
                {/* Active Indicator Micro Dot below icon */}
                {isActive && (
                  <div className="absolute bottom-1 w-1.5 h-1.5 rounded-full bg-brown-900 animate-scale-up" />
                )}
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default DashboardLayout;